import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { useSettingsModalEmbedNavigation } from 'components/settings_modal_embed_context'
import { useProductRefreshSettingsIa } from 'features/app_shell/product_refresh/useProductRefreshSettingsIa'
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path'

import {
  isSettingsModalNavigationActive,
  mergeSettingsSearch,
  parseSettingsSectionFromSearch,
  parseSettingsTabFromSearch,
} from './constants'
import { matchLegacySettingsPathToSettingsSection } from './legacy_settings_routes'
import { confirmDiscardIfDirty } from './use_settings_modal_url_normalization'

function changesSettingsSelection(currentSearch, target) {
  if (target == null || typeof target === 'string') {
    return false
  }

  const targetSearch = target.search ?? ''
  return (
    parseSettingsSectionFromSearch(currentSearch) !== parseSettingsSectionFromSearch(targetSearch)
    || parseSettingsTabFromSearch(currentSearch) !== parseSettingsTabFromSearch(targetSearch)
  )
}

const LEGACY_SETTINGS_URL_BASE = 'https://calendly.local'

const parseLegacyPath = (legacyPath) => {
  if (typeof legacyPath !== 'string') {
    return null
  }
  try {
    return new URL(legacyPath, LEGACY_SETTINGS_URL_BASE)
  } catch (_error) {
    return null
  }
}

const mergeLegacyPathSearch = (existingSearch, legacyPath) => {
  const url = parseLegacyPath(legacyPath)
  if (url == null || !url.search) {
    return existingSearch
  }
  const params = new URLSearchParams(existingSearch)
  url.searchParams.forEach((value, key) => {
    params.set(key, value)
  })
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Resolves a Link / history target: query-style on the current pathname when the modal flow is active,
 * otherwise the legacy path string.
 * @param {{
 *   section: string,
 *   legacyPath: string,
 *   location: { pathname: string, search: string, hash: string },
 *   settingsTab?: string | null,
 *   isRebrandSettingsIaEnabled: boolean,
 * }} args
 * @returns {string | { pathname: string, search: string, hash: string }}
 */
export function resolveSettingsNavigationTarget({
  section,
  legacyPath,
  location,
  settingsTab,
  isRebrandSettingsIaEnabled,
}) {
  if (isSettingsModalNavigationActive({ isRebrandSettingsIaEnabled, search: location.search })) {
    const searchWithLegacyParams = mergeLegacyPathSearch(location.search, legacyPath)
    return {
      pathname: location.pathname,
      search: mergeSettingsSearch(section, searchWithLegacyParams, settingsTab),
      hash: location.hash,
    }
  }
  return legacyPath
}

/**
 * Resolves settings entry links that should open the modal whenever the rebranded settings
 * experience is enabled (for example profile menu shortcuts).
 * @param {{
 *   section: string,
 *   legacyPath: string,
 *   location: { pathname: string, search: string, hash: string },
 *   settingsTab?: string | null,
 *   isRebrandSettingsIaEnabled: boolean,
 * }} args
 * @returns {string | { pathname: string, search: string, hash: string }}
 */
function resolveSettingsModalEntryTarget({
  section,
  legacyPath,
  location,
  settingsTab,
  isRebrandSettingsIaEnabled,
}) {
  if (isRebrandSettingsIaEnabled) {
    const searchWithLegacyParams = mergeLegacyPathSearch(location.search, legacyPath)
    return {
      pathname: location.pathname,
      search: mergeSettingsSearch(section, searchWithLegacyParams, settingsTab),
      hash: location.hash,
    }
  }
  return legacyPath
}

/**
 * Resolves a legacy settings path through the same section mapping used by the modal.
 * Unmatched paths fall back unchanged so standalone flows like `/app/admin/invitations/new`
 * keep their legacy behavior.
 * @param {{
 *   legacyPath: string,
 *   location: { pathname: string, search: string, hash: string },
 *   isRebrandSettingsIaEnabled: boolean,
 * }} args
 * @returns {string | { pathname: string, search: string, hash: string }}
 */
function resolveLegacySettingsNavigationTarget({
  legacyPath,
  location,
  isRebrandSettingsIaEnabled,
}) {
  const match = matchLegacySettingsPathToSettingsSection(legacyPath)
  if (match == null) {
    return legacyPath
  }
  return resolveSettingsNavigationTarget({
    section: match.section,
    legacyPath,
    location,
    settingsTab: match.settingsTab ?? undefined,
    isRebrandSettingsIaEnabled,
  })
}

/**
 * Converts a React Router target into a concrete href for anchor surfaces that still need one.
 * Click handlers should still use history navigation for object targets when possible.
 * @param {string | { pathname: string, search?: string, hash?: string }} target
 * @returns {string}
 */
export function settingsNavigationTargetToHref(target) {
  if (typeof target === 'string') {
    return target
  }
  return `${target.pathname}${target.search ?? ''}${target.hash ?? ''}`
}

export const isPlainLeftClick = (event) => (
  event.button === 0
  && !event.metaKey
  && !event.altKey
  && !event.ctrlKey
  && !event.shiftKey
)

export const getSettingsNavigationLinkProps = ({ target, navigate }) => ({
  href: settingsNavigationTargetToHref(target),
  onClick: (event) => {
    if (event.defaultPrevented || typeof target === 'string' || !isPlainLeftClick(event)) {
      return
    }
    event.preventDefault()
    navigate(target)
  },
})

/**
 * Client-side settings navigation: use query params when the settings modal flow is active
 * (`ap_rebrand_settings_ia` + `?settings=`), otherwise legacy routes.
 */
export function useSettingsNavigation() {
  const location = useLocation()
  const history = useHistory()
  const embeddedNavigation = useSettingsModalEmbedNavigation()
  const navigationLocation = embeddedNavigation.browserLocation ?? location
  const navigationHistory = embeddedNavigation.browserHistory ?? history
  const meetingTypesPath = useMeetingTypesPath()
  const isRebrandSettingsIaEnabled = useProductRefreshSettingsIa()

  const isSettingsModalNavigationActiveValue = useMemo(
    () => isSettingsModalNavigationActive({
      isRebrandSettingsIaEnabled,
      search: navigationLocation.search,
    }),
    [isRebrandSettingsIaEnabled, navigationLocation.search],
  )

  const hrefForSettingsSection = useCallback(
    (section, legacyPath, settingsTab) => resolveSettingsNavigationTarget({
      section,
      legacyPath,
      location: navigationLocation,
      settingsTab,
      isRebrandSettingsIaEnabled,
    }),
    [navigationLocation, isRebrandSettingsIaEnabled],
  )

  const hrefForSettingsModalEntry = useCallback(
    (section, legacyPath, settingsTab) => resolveSettingsModalEntryTarget({
      section,
      legacyPath,
      location: navigationLocation,
      settingsTab,
      isRebrandSettingsIaEnabled,
    }),
    [navigationLocation, isRebrandSettingsIaEnabled],
  )

  const hrefForSettingsPath = useCallback(
    (legacyPath) => resolveLegacySettingsNavigationTarget({
      legacyPath,
      location: navigationLocation,
      isRebrandSettingsIaEnabled,
    }),
    [navigationLocation, isRebrandSettingsIaEnabled],
  )

  const navigateToTarget = useCallback(
    (target, replace) => {
      if (
        isSettingsModalNavigationActiveValue
        && changesSettingsSelection(navigationLocation.search, target)
        && !confirmDiscardIfDirty()
      ) {
        return false
      }

      if (replace) {
        navigationHistory.replace(target)
      } else {
        navigationHistory.push(target)
      }
      return true
    },
    [
      isSettingsModalNavigationActiveValue,
      navigationHistory,
      navigationLocation.search,
    ],
  )

  const navigateToSettingsSection = useCallback(
    (section, { legacyPath, settingsTab, replace = false } = {}) => {
      const target = resolveSettingsNavigationTarget({
        section,
        legacyPath,
        location: navigationLocation,
        settingsTab,
        isRebrandSettingsIaEnabled,
      })

      return navigateToTarget(target, replace)
    },
    [navigationLocation, isRebrandSettingsIaEnabled, navigateToTarget],
  )

  const navigateToSettingsModalEntry = useCallback(
    (section, { legacyPath, settingsTab, replace = false } = {}) => {
      const target = resolveSettingsModalEntryTarget({
        section,
        legacyPath,
        location: navigationLocation,
        settingsTab,
        isRebrandSettingsIaEnabled,
      })
      return navigateToTarget(target, replace)
    },
    [navigationLocation, isRebrandSettingsIaEnabled, navigateToTarget],
  )

  const navigateToSettingsPath = useCallback(
    (legacyPath, { replace = false } = {}) => {
      const target = resolveLegacySettingsNavigationTarget({
        legacyPath,
        location: navigationLocation,
        isRebrandSettingsIaEnabled,
      })
      return navigateToTarget(target, replace)
    },
    [navigationLocation, isRebrandSettingsIaEnabled, navigateToTarget],
  )

  const legacyPersonalUrlRedirectTo = useCallback(
    (section, settingsTab) => ({
      pathname: meetingTypesPath,
      search: mergeSettingsSearch(section, navigationLocation.search, settingsTab),
      hash: navigationLocation.hash,
    }),
    [meetingTypesPath, navigationLocation.hash, navigationLocation.search],
  )

  return {
    isSettingsModalNavigationActive: isSettingsModalNavigationActiveValue,
    hrefForSettingsSection,
    hrefForSettingsModalEntry,
    hrefForSettingsPath,
    legacyPersonalUrlRedirectTo,
    navigateToSettingsSection,
    navigateToSettingsModalEntry,
    navigateToSettingsPath,
  }
}
